import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Container,
  Typography,
  Alert,
  TextField,
  Box,
  Grid,
  Card,
  CardContent,
  CardHeader,
  Avatar,
  Chip,
  Stack,
  Tooltip,
} from "@mui/material";

// Génère les initiales pour l'avatar
const getInitials = (prenom, nom) => {
  if (!prenom && !nom) return "?";
  return `${prenom ? prenom[0] : ""}${nom ? nom[0] : ""}`.toUpperCase();
};

// Badge de statut coloré
const StatusChip = ({ statut }) => {
  if (!statut) return <Chip label="Inconnu" color="default" />;
  const txt = statut.charAt(0).toUpperCase() + statut.slice(1);

  if (/valid|accept/i.test(statut)) {
    // validé/accepté
    return (
      <Chip
        label={txt}
        style={{
          background: "#e9f9ef",
          color: "#22c55e",
          fontWeight: 700,
          border: "1.2px solid #22c55e",
          fontSize: 15,
          letterSpacing: "1px",
        }}
      />
    );
  }
  if (/refus|rej/i.test(statut)) {
    // refusé/rejeté
    return (
      <Chip
        label={txt}
        style={{
          background: "#ffeaea",
          color: "#ff3b3b",
          fontWeight: 700,
          border: "1.2px solid #ff3b3b",
          fontSize: 15,
          letterSpacing: "1px",
        }}
      />
    );
  }
  // "En attente" ou tout autre
  return (
    <Chip
      label={txt}
      style={{
        background: "#fff8e2",
        color: "#f59e0b",
        fontWeight: 700,
        border: "1.2px solid #fbbf24",
        fontSize: 15,
        letterSpacing: "1px",
      }}
    />
  );
};

function Dashboard() {
  const [demandes, setDemandes] = useState([]);
  const [filteredDemandes, setFilteredDemandes] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    const fetchDemandes = async () => {
      try {
        const token = localStorage.getItem("accessToken");
        if (!token) {
          setError("Utilisateur non authentifié");
          return;
        }
        const response = await axios.get("https://appapi.unpaidfinancial.com/api/demandes", {
          headers: { Authorization: `Bearer ${token}` },
        });
        setDemandes(response.data);
        setFilteredDemandes(response.data);
      } catch (err) {
        setError("Erreur lors de la récupération des demandes");
        console.error(err);
      }
    };
    fetchDemandes();
  }, []);

  const handleSearch = (event) => {
    const value = event.target.value.toLowerCase();
    setSearchTerm(value);
    const filtered = demandes.filter(
      (demande) =>
        (demande.nom || "").toLowerCase().includes(value) ||
        (demande.prenom || "").toLowerCase().includes(value) ||
        (demande.email || "").toLowerCase().includes(value)
    );
    setFilteredDemandes(filtered);
  };

  return (
    <Container maxWidth="xl" sx={{ pt: 2 }}>
      <Typography
        variant="h4"
        sx={{
          fontWeight: 800,
          color: "#23253a",
          letterSpacing: ".3px",
          mb: 3,
          textAlign: "center",
        }}
      >
        Liste des demandes clients
      </Typography>
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Box
        sx={{
          my: 3,
          maxWidth: 480,
          mx: "auto",
        }}
      >
        <TextField
          label="Recherche (nom, prénom ou email)"
          variant="outlined"
          fullWidth
          value={searchTerm}
          onChange={handleSearch}
          sx={{
            bgcolor: "#fff",
            borderRadius: 2,
            boxShadow: "0 1.5px 10px rgba(44,44,92,0.03)",
          }}
        />
      </Box>

      <Grid container spacing={4}>
        {filteredDemandes.length === 0 && (
          <Grid item xs={12}>
            <Alert severity="info" sx={{ textAlign: "center" }}>
              Aucune demande trouvée.
            </Alert>
          </Grid>
        )}
        {filteredDemandes.map((demande) => (
          <Grid item xs={12} sm={6} md={4} key={demande.id}>
            <Card
              elevation={4}
              sx={{
                height: "100%",
                transition: "transform 0.18s, box-shadow 0.23s",
                "&:hover": {
                  transform: "translateY(-6px) scale(1.03)",
                  boxShadow: "0 8px 32px 0 rgba(44,44,92,0.13)",
                },
                borderRadius: "17px",
                bgcolor: "#f9fafb",
              }}
            >
              <CardHeader
                avatar={
                  <Tooltip title={demande.email || ""}>
                    <Avatar
                      sx={{
                        bgcolor: "#647dee",
                        color: "#fff",
                        fontWeight: 700,
                        fontSize: 25,
                        width: 54,
                        height: 54,
                        border: "3.2px solid #fff",
                        boxShadow: "0 3px 8px rgba(100,100,100,0.13)",
                      }}
                    >
                      {getInitials(demande.prenom, demande.nom)}
                    </Avatar>
                  </Tooltip>
                }
                title={
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 700, color: "#31314e" }}
                  >
                    {demande.nom} {demande.prenom}
                  </Typography>
                }
                subheader={
                  <Typography variant="body2" sx={{ color: "#7b7b8d" }}>
                    {demande.email}
                  </Typography>
                }
              />
              <CardContent>
                <Stack spacing={1.2}>
                  <Typography variant="body1" sx={{ fontSize: "1rem" }}>
                    <strong>Téléphone :</strong>{" "}
                    {demande.telephone || (
                      <span style={{ color: "#bbb" }}>Non renseigné</span>
                    )}
                  </Typography>

                  <Typography variant="body1" sx={{ fontSize: "1rem" }}>
                    <strong>Sexe :</strong> {demande.sexe}
                  </Typography>

                  <Typography variant="body1" sx={{ fontSize: "1rem" }}>
                    <strong>Date de Naissance :</strong> {demande.dateNaissance}
                  </Typography>

                  <Typography variant="body1" sx={{ fontSize: "1rem" }}>
                    <strong>Adresse résidentielle :</strong> {demande.adresse}
                  </Typography>

                  {(demande.ville || demande.pays) && (
                    <Typography variant="body1" sx={{ fontSize: "1rem" }}>
                      <strong>Localisation :</strong> {demande.ville || ""}{" "}
                      {demande.pays && `, ${demande.pays}`}
                    </Typography>
                  )}
                  <Typography variant="body1" sx={{ fontSize: "1rem" }}>
                    <strong>Type d'aide :</strong> {demande.typeAide}
                  </Typography>

                  {demande.montantDemande && (
                    <Typography variant="body1" sx={{ fontSize: "1rem" }}>
                      <strong>Montant :</strong> {demande.montantDemande}{" "}
                      {demande.devise}
                    </Typography>
                  )}

                  <Typography variant="body1" sx={{ fontSize: "1rem" }}>
                    <strong>Créée le :</strong>{" "}
                    {new Date(demande.created_at).toLocaleString("fr-FR", {
                      day: "2-digit",
                      month: "2-digit",
                      year: "numeric",
                      hour: "2-digit",
                      minute: "2-digit",
                      hour12: false,
                    })}
                  </Typography>

                  {/* Affichage du statut ici, centré en bas */}
                  <Box sx={{ mt: 2, textAlign: "center" }}>
                    <StatusChip statut={demande.statut} />
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Container>
  );
}

export default Dashboard;
